<template>
  <div class="pageRoot linkListPage collectionModule">
    <div class="pageShell" ref="pageCard">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <style-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="样式名称"
                    prop="name"
                    align="center"
                    header-align="center"
                    min-width="180"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.name || '—' }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="样式类型"
                    prop="styleType"
                    align="center"
                    header-align="center"
                    min-width="120"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="'typeTag--' + getStyleTypeVariant(scope.row.styleType)"
                      >
                        {{ scope.row.styleType || '—' }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="展示方式"
                    prop="displayMode"
                    align="center"
                    header-align="center"
                    min-width="120"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="cellMuted">{{
                        getDisplayModeLabel(scope.row.displayMode)
                      }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="网格"
                    prop="columNum"
                    align="center"
                    header-align="center"
                    width="88"
                  >
                    <template slot-scope="scope">
                      <span class="gridSizeCell">{{
                        formatGridSize(scope.row.columNum)
                      }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="标题"
                    align="center"
                    header-align="center"
                    width="100"
                  >
                    <template slot-scope="scope">
                      <span
                        class="statusPill"
                        :class="
                          isShowTitleEnabled(scope.row.isShowTitle)
                            ? 'statusPill--online'
                            : 'statusPill--offline'
                        "
                      >
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ getShowTitleLabel(scope.row.isShowTitle) }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="280"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-view"
                          @click.stop="lookInfo(scope.row.id)"
                        >
                          查看
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-edit"
                          v-check-role
                          @click.stop="openEdit(scope.row.id)"
                        >
                          编辑
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost rowActionBtn--danger"
                          icon="el-icon-delete"
                          v-check-role
                          @click.stop="deleteStyleRow(scope.row.id)"
                        >
                          删除
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-s-grid" /></div>
                <p class="tableEmpty__title">暂无样式</p>
                <p class="tableEmpty__desc">调整筛选条件或新增样式。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="dialogOpen()"
                >
                  新增样式
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <style-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <style-form-dialog
      :visible.sync="dialogVisible"
      :loading="dialogLoading"
      :mode="nowStatus"
      :rule-form="ruleForm"
      :rules="rules"
      :style-type-options="styleTypeOptions"
      @close="handleClose"
      @edit="changeEdit"
      @submit="submit"
    />
  </div>
</template>

<script>
import {
  getStyleList,
  listStyleType,
  getStyleById,
  createStyle,
  editStyle,
  deleteStyleById,
} from '../../../api/styleManage'
import StyleListFilterPanel from './StyleListFilterPanel.vue'
import StyleFormDialog from './StyleFormDialog.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'
import {
  formatGridSize,
  getDisplayModeLabel,
  getShowTitleLabel,
  getStyleTypeVariant,
  isShowTitleEnabled,
} from './styleListHelpers'

export default {
  name: 'StyleManageIndex',
  components: {
    StyleListFilterPanel,
    StyleFormDialog,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      styleName: '',
      styleType: '',
      displayMode: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
      styleTypeOptions: [],
      dialogVisible: false,
      ruleForm: {
        name: '',
        styleType: '',
        displayMode: '',
        isShowTitle: '',
        columNum: '',
      },
      rules: {
        name: [{ required: true, message: '样式名称不能为空', trigger: 'blur' }],
        styleType: [{ required: true, message: '样式类型不能为空', trigger: 'change' }],
        displayMode: [{ required: true, message: '请选择展示方式', trigger: 'change' }],
        isShowTitle: [{ required: true, message: '请选择是否显示标题', trigger: 'change' }],
        columNum: [{ required: true, message: '网格行列数不能为空', trigger: 'blur' }],
      },
      /** 0 创建，1 查看，2 修改 */
      nowStatus: 0,
      dialogLoading: false,
      id: '',
      /** mixin 兼容：样式列表无跨页勾选 */
      multipleSelection: [],
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.styleName, this.styleType, this.displayMode].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.styleName || '').trim()) {
        chips.push({ key: 'styleName', label: '样式名称', value: this.styleName.trim() })
      }
      if (this.styleType !== '' && this.styleType != null) {
        chips.push({
          key: 'styleType',
          label: '样式类型',
          value: String(this.styleType),
        })
      }
      if (String(this.displayMode || '').trim()) {
        chips.push({
          key: 'displayMode',
          label: '展示方式',
          value: this.displayMode.trim(),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        styleName: this.styleName,
        styleType: this.styleType,
        displayMode: this.displayMode,
        activeFilterCount: this.activeFilterCount,
        styleTypeOptions: this.styleTypeOptions,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.dialogOpen(),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:styleName': (value) => {
          this.styleName = value
        },
        'update:styleType': (value) => {
          this.styleType = value
        },
        'update:displayMode': (value) => {
          this.displayMode = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getstyleType()
    this.getList()
  },
  methods: {
    getStyleTypeVariant,
    formatGridSize,
    getDisplayModeLabel,
    getShowTitleLabel,
    isShowTitleEnabled,
    getList() {
      this.loading = true
      getStyleList({
        currentPage: this.currentPage,
        currentPageSize: this.currentPageSize,
        styleName: this.styleName,
        styleType: this.styleType,
        displayMode: this.displayMode,
      })
        .then((res) => {
          if (res.success) {
            this.total = res.total
            this.tableData = res.data || []
          }
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.styleName = ''
      this.styleType = ''
      this.displayMode = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    getstyleType() {
      listStyleType().then((res) => {
        if (res.success) {
          this.styleTypeOptions = res.data || []
        }
      })
    },
    lookInfo(id) {
      this.id = id
      this.dialogOpen(id)
    },
    openEdit(id) {
      this.id = id
      this.dialogOpen(id)
      this.nowStatus = 2
    },
    handleClose() {
      this.ruleForm = {
        name: '',
        styleType: '',
        displayMode: '',
        isShowTitle: '',
        columNum: '',
      }
      this.id = ''
      this.dialogVisible = false
    },
    dialogOpen(id = '') {
      if (id) {
        this.nowStatus = 1
        this.dialogLoading = true
        getStyleById({ id })
          .then((res) => {
            if (res.success) {
              const data = { ...res.data }
              data.isShowTitle = data.isShowTitle + ''
              data.columNum = data.columNum + ''
              data.styleType = data.styleType == null ? '' : String(data.styleType)
              this.ruleForm = data
            }
          })
          .finally(() => {
            this.dialogLoading = false
          })
      } else {
        this.nowStatus = 0
        this.ruleForm = {
          name: '',
          styleType: '',
          displayMode: '资源展示',
          isShowTitle: '1',
          columNum: '1',
        }
      }
      this.dialogVisible = true
    },
    changeEdit() {
      this.nowStatus = 2
    },
    submit() {
      const isCreate = this.nowStatus === 0
      const request = isCreate
        ? createStyle(this.ruleForm)
        : editStyle(this.id, this.ruleForm)
      request.then((res) => {
        if (res.success) {
          this.$message.success(isCreate ? '新建成功' : '保存成功')
          this.handleClose()
          this.getList()
          this.getstyleType()
        }
      })
    },
    deleteStyleRow(id) {
      this.$confirm('此操作将删除该样式，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => deleteStyleById({ id }))
        .then((res) => {
          if (res && res.success) {
            this.$message.success('删除成功')
            if (this.dialogVisible && String(this.id) === String(id)) {
              this.handleClose()
            }
            this.getList()
            this.getstyleType()
          }
        })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.gridSizeCell {
  font-variant-numeric: tabular-nums;
  color: var(--link-ink-secondary);
  font-family: var(--link-font-mono, ui-monospace, monospace);
}

.cellMuted {
  color: var(--link-ink-secondary);
}
</style>
